// src/components/PMTracker.js
import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../AuthContext';
import { PHASES, PHASE_COLORS, newClient } from '../constants';
import ClientCard from './ClientCard';
import ClientModal from './ClientModal';

// re-export alertLevel for local use
function alertLevel(c) {
  if (c.onHold || c.phase === 'postlaunch' || c.phase === 'cancelled') return '';
  const d = c.lastChecked ? Math.floor((new Date() - new Date(c.lastChecked)) / 86400000) : 999;
  if (d >= 7) return 'urgent';
  if (d >= 4) return 'stale';
  return '';
}

function sortCards(arr) {
  return [...arr].sort((a, b) => {
    const ai = a.important ? 0 : 1, bi = b.important ? 0 : 1;
    if (ai !== bi) return ai - bi;
    const av = alertLevel(a) === 'urgent' ? 0 : alertLevel(a) === 'stale' ? 1 : 2;
    const bv = alertLevel(b) === 'urgent' ? 0 : alertLevel(b) === 'stale' ? 1 : 2;
    return av - bv;
  });
}

export default function PMTracker({ allUsers }) {
  const { user, userProfile } = useAuth();
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalClient, setModalClient] = useState(null);
  const [search, setSearch] = useState('');
  const [filterPhase, setFilterPhase] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState('board');
  const [toast, setToast] = useState('');

  const isPM = userProfile?.role === 'pm' || userProfile?.role === 'admin';

  // Listen to ALL clients that have opspm = true
  useEffect(() => {
    const q = query(collection(db, 'clients'), where('opspm', '==', true));
    const unsub = onSnapshot(q, snap => {
      setAllClients(snap.docs.map(d => ({ ...d.data(), id: d.id })));
      setLoading(false);
    });
    return unsub;
  }, []);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 2400);
  }

  function ownerName(uid) {
    const u = allUsers.find(u => u.uid === uid);
    return u ? (u.displayName || u.email) : '—';
  }

  async function handleSave(data) {
    function sanitize(obj) {
      if (Array.isArray(obj)) return obj.map(sanitize);
      if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(
          Object.entries(obj)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, sanitize(v)])
        );
      }
      return obj;
    }
    try {
      const ref = doc(db, 'clients', data.id);
      await setDoc(ref, sanitize(data), { merge: true });
      showToast(`Saved: ${data.name} ✓`);
    } catch (e) {
      console.error('Save error:', e);
      showToast('⚠ Save failed: ' + e.message);
    }
  }

  function openNew() {
    // Default to first non-viewer, non-pm user as owner, PM can reassign
    const defaultOwner = allUsers.find(u => u.role === 'user' || u.role === 'admin');
    const c = newClient(defaultOwner?.uid || user.uid, defaultOwner?.displayName || defaultOwner?.email || '');
    // Pre-check opspm so it shows in PM tracker immediately
    setModalClient({ ...c, opspm: true });
  }

  async function handleDuplicate() {
    if (!modalClient) return;
    const newId = Date.now().toString(36) + Math.random().toString(36).slice(2);
    const duplicate = {
      ...modalClient,
      id: newId,
      name: modalClient.name + ' (Copy)',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      actualLaunch: '',
      kickoff: '',
      projLaunch: '',
      lastChecked: '',
    };
    function sanitize(obj) {
      if (Array.isArray(obj)) return obj.map(sanitize);
      if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(Object.entries(obj).filter(([,v]) => v !== undefined).map(([k,v]) => [k, sanitize(v)]));
      }
      return obj;
    }
    await setDoc(doc(db, 'clients', newId), sanitize(duplicate));
    setModalClient(duplicate);
    showToast(`Duplicated: ${duplicate.name} ✓`);
  }

  async function handleDelete() {
    if (!modalClient) return;
    await deleteDoc(doc(db, 'clients', modalClient.id));
    setModalClient(null);
    showToast('Deleted');
  }

  async function toggleImportant(id) {
    if (!isPM) return;
    const c = allClients.find(x => x.id === id);
    if (!c) return;
    await setDoc(doc(db, 'clients', id), { important: !c.important, updatedAt: Date.now() }, { merge: true });
  }

  const filtered = allClients.filter(c => {
    const q = search.toLowerCase();
    const mq = !q || c.name?.toLowerCase().includes(q) || (c.notes || '').toLowerCase().includes(q);
    const mph = !filterPhase || c.phase === filterPhase;
    let ms = true;
    if (filterStatus === 'hold') ms = !!c.onHold;
    else if (filterStatus === 'active') ms = !c.onHold;
    else if (filterStatus === 'urgent') ms = alertLevel(c) === 'urgent';
    return mq && mph && ms;
  });

  // Stats
  const active = allClients.filter(c => !c.onHold && c.phase !== 'postlaunch').length;
  const onHold = allClients.filter(c => c.onHold).length;
  const urgent = allClients.filter(c => alertLevel(c) === 'urgent').length;
  const postlaunch = allClients.filter(c => c.phase === 'postlaunch').length;

  const selStyle = {
    fontSize: 12, padding: '5px 8px', border: '1px solid rgba(0,0,0,0.18)',
    borderRadius: 7, background: '#f5f5f3', color: '#1a1a18', fontFamily: 'inherit',
  };

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
      Loading PM tracker…
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* PM banner */}
      <div style={{
        background: '#EAF3DE', borderBottom: '1px solid #b8dfa0',
        padding: '5px 16px', fontSize: 11, color: '#27500A', fontWeight: 600,
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        📋 Project Manager Tracker — showing all accounts with Ops PM checked
        <span style={{ marginLeft: 4, color: '#888', fontWeight: 400 }}>
          (excluded from Global Overview counts)
        </span>
      </div>

      {/* Stats bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.08)',
        padding: '6px 14px', display: 'flex', gap: 5, flexWrap: 'wrap',
        alignItems: 'center', flexShrink: 0,
      }}>
        {[
          { label: 'Active', value: active, bg: '#EAF3DE', color: '#27500A', click: () => setFilterStatus('active') },
          { label: 'On Hold', value: onHold, bg: '#FCEBEB', color: '#A32D2D', click: () => setFilterStatus('hold') },
          { label: '🚀 Launched', value: postlaunch, bg: '#dcf0e8', color: '#0F6E56', click: () => setFilterPhase('postlaunch') },
          { label: '⚠ Urgent', value: urgent, bg: '#FCEBEB', color: '#A32D2D', click: () => setFilterStatus('urgent') },
        ].map(s => (
          <div key={s.label} onClick={s.click} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: s.bg, borderRadius: 6, padding: '3px 9px',
            cursor: 'pointer', border: '1px solid transparent',
          }}
            onMouseEnter={e => e.currentTarget.style.border = `1px solid ${s.color}55`}
            onMouseLeave={e => e.currentTarget.style.border = '1px solid transparent'}
          >
            <span style={{ fontSize: 16, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: s.color }}>{s.label}</span>
          </div>
        ))}
        {(filterStatus || filterPhase) && (
          <button onClick={() => { setFilterStatus(''); setFilterPhase(''); }} style={{
            fontSize: 10, padding: '3px 8px', background: 'none',
            border: '1px solid rgba(0,0,0,0.18)', borderRadius: 5,
            cursor: 'pointer', color: '#888', fontFamily: 'inherit',
          }}>✕ Clear</button>
        )}
      </div>

      {/* Toolbar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.1)',
        padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexShrink: 0,
      }}>
        {isPM && (
          <button onClick={openNew} style={{
            fontSize: 12, padding: '5px 12px', background: '#27500A', color: '#fff',
            border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700,
            fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}>
            + New Client
          </button>
        )}
        <input
          type="text"
          placeholder="Search PM accounts…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...selStyle, width: 200 }}
        />
        <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} style={selStyle}>
          <option value="">All phases</option>
          {PHASES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex' }}>
          {['board', 'list'].map(v => (
            <button key={v} onClick={() => setViewMode(v)} style={{
              fontSize: 12, padding: '5px 10px',
              background: viewMode === v ? '#EAF3DE' : 'transparent',
              color: viewMode === v ? '#27500A' : '#888',
              border: '1px solid rgba(0,0,0,0.18)',
              borderLeft: v !== 'board' ? 'none' : '1px solid rgba(0,0,0,0.18)',
              borderRadius: v === 'board' ? '7px 0 0 7px' : '0 7px 7px 0',
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: viewMode === v ? 700 : 400,
            }}>
              {v === 'board' ? '⊟ Board' : '☰ List'}
            </button>
          ))}
        </div>
      </div>

      {/* Board / List */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {viewMode === 'board' ? (
          <div style={{ flex: 1, overflowX: 'auto', display: 'flex', gap: 8, padding: 10 }}>
            {PHASES.map(phase => {
              const cards = sortCards(filtered.filter(c => c.phase === phase.id));
              return (
                <div key={phase.id} style={{
                  flexShrink: 0, width: 224, display: 'flex', flexDirection: 'column',
                  background: '#f5f5f3', borderRadius: 12,
                  border: '1px solid rgba(0,0,0,0.1)', overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '7px 10px', borderBottom: '1px solid rgba(0,0,0,0.1)',
                    display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                  }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: phase.color, flexShrink: 0 }} />
                    <div style={{ fontSize: 11, fontWeight: 700, flex: 1 }}>{phase.label}</div>
                    <div style={{
                      fontSize: 11, color: '#888', background: '#fff',
                      border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, padding: '1px 6px',
                    }}>{cards.length}</div>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {cards.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '18px 8px', color: '#ccc', fontSize: 11 }}>No accounts</div>
                    ) : cards.map(c => (
                      <ClientCard
                        key={c.id}
                        client={c}
                        onClick={() => setModalClient({ ...c })}
                        onToggleImportant={toggleImportant}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            <table style={{
              width: '100%', borderCollapse: 'collapse', fontSize: 12,
              background: '#fff', borderRadius: 12, overflow: 'hidden',
              border: '1px solid rgba(0,0,0,0.1)',
            }}>
              <thead>
                <tr>
                  {['Client', 'Owner', 'Phase', 'CSM', 'Last Checked', 'PM Notes'].map(h => (
                    <th key={h} style={{
                      textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700,
                      color: '#888', whiteSpace: 'nowrap', background: '#f5f5f3',
                      borderBottom: '1px solid rgba(0,0,0,0.1)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortCards(filtered).map(c => {
                  const al = alertLevel(c);
                  const ph = PHASES.find(p => p.id === c.phase);
                  const phC = PHASE_COLORS[c.phase] || {};
                  return (
                    <tr key={c.id} onClick={() => setModalClient({ ...c })} style={{ cursor: 'pointer' }}
                      onMouseEnter={e => [...e.currentTarget.cells].forEach(td => td.style.background = '#f8f8f8')}
                      onMouseLeave={e => [...e.currentTarget.cells].forEach(td => td.style.background = '')}
                    >
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)', borderLeft: al === 'urgent' ? '3px solid #E24B4A' : al === 'stale' ? '3px solid #EF9F27' : undefined }}>
                        <strong>{c.name}</strong>
                        {c.pinNote && <div style={{ fontSize: 11, color: '#854F0B' }}>📌 {c.pinNote}</div>}
                      </td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 11, color: '#888' }}>{ownerName(c.ownerUid)}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 700, background: phC.bg, color: phC.text, whiteSpace: 'nowrap' }}>
                          {ph?.label || c.phase}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 11, color: '#888' }}>{c.csm || '—'}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 11, color: al === 'urgent' ? '#A32D2D' : '#888', fontWeight: al === 'urgent' ? 700 : 400 }}>{c.lastChecked || '—'}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 11, color: '#27500A', maxWidth: 200 }}>
                        {c.pmNotes ? (c.pmNotes.length > 60 ? c.pmNotes.slice(0, 60) + '…' : c.pmNotes) : <span style={{ color: '#ccc' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20,
          background: '#27500A', color: '#fff', padding: '9px 16px',
          borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 300,
        }}>{toast}</div>
      )}

      {/* Modal */}
      {modalClient && (
        <ClientModal
          client={modalClient}
          onSave={handleSave}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onClose={() => setModalClient(null)}
        />
      )}
    </div>
  );
}
